<template>
  <div class="home">
    <div class="container">
      <div class="payment-form">
        <div class="payment-info">
          <p class="payee">付款给：VNOcan</p>
        </div>
        <div class="form-group">
          <label for="amount">金额（元）</label>
          <input 
            type="number" 
            id="amount" 
            v-model.number="amount" 
            placeholder="请输入金额"
            min="0.01"
            step="0.01"
          />
        </div>
        <button @click="processPayment" :disabled="loading" class="pay-button">
          {{ loading ? '处理中...' : '立即支付' }}
        </button>
      </div>
      
      <div class="result" :class="resultClass" v-if="result.message">
        <strong>{{ result.type }}:</strong> {{ result.message }}
        <div v-if="qrCodeUrl" class="qr-code-container">
          <!-- 使用iframe方式显示支付宝二维码，提高兼容性 -->
          <div v-if="isAlipayQrCode" class="alipay-qr-container">
            <iframe :src="qrCodeUrl" frameborder="0" class="qr-iframe" @load="onIframeLoad" @error="onQrCodeError"></iframe>
          </div>
          <!-- 其他二维码仍然使用img标签 -->
          <img v-else :src="qrCodeUrl" alt="支付二维码" class="qr-code" @error="onQrCodeError" />
          <p>请使用支付宝扫描二维码完成支付</p>
          <!-- 添加二维码链接文本，方便用户手动复制 -->
          <p class="qr-link-text">二维码链接: {{ qrCodeUrl }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'Home',
  props: {
    // 接收路由参数
    openId: {
      type: String,
      default: ''
    },
    accessToken: {
      type: String,
      default: ''
    },
    error: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      amount: null, // 默认值设为null，不显示具体数值
      loading: false,
      result: {
        type: '',
        message: ''
      },
      qrCodeUrl: '',
      isAlipayQrCode: false // 标记是否为支付宝二维码
    };
  },
  computed: {
    resultClass() {
      return this.result.type === 'success' ? 'success' : 'error';
    }
  },
  mounted() {
    // 检查URL参数中是否包含微信授权回调信息
    this.processUrlParams();
  },
  methods: {
    processUrlParams() {
      // 优先使用路由参数
      if (this.openId) {
        // 如果有openId参数，说明是微信授权回调，直接处理支付
        this.processWechatPaymentWithOpenId(this.openId);
        return;
      }
      
      // 检查URL参数中是否包含错误信息
      const urlParams = new URLSearchParams(window.location.search);
      const error = urlParams.get('error');
      if (error) {
        this.result = {
          type: 'error',
          message: decodeURIComponent(error)
        };
      }
    },
    
    // 从本地存储获取openId
    getOpenIdFromStorage() {
      try {
        return localStorage.getItem('wechat_openid');
      } catch (e) {
        return null;
      }
    },
    
    // 保存openId到本地存储
    saveOpenIdToStorage(openId) {
      try {
        localStorage.setItem('wechat_openid', openId);
      } catch (e) {
        console.error('保存openId失败:', e);
      }
    },
    
    // 二维码加载错误处理
    onQrCodeError(event) {
      console.error('二维码加载失败:', event);
      this.result = {
        type: 'error',
        message: '二维码加载失败，请尝试以下方法：1. 检查网络连接 2. 手动复制下方链接到支付宝 3. 刷新页面重试'
      };
    },
    
    // iframe加载完成
    onIframeLoad(event) {
      console.log('iframe加载完成:', event);
    },
    
    async processPayment() {
      // 验证金额
      if (!this.amount || this.amount <= 0) {
        this.result = {
          type: 'error',
          message: '请输入有效的支付金额'
        };
        return;
      }
      
      // 将元转换为分
      const amountInCents = Math.round(this.amount * 100);
      
      this.loading = true;
      this.result = { type: '', message: '' };
      this.qrCodeUrl = '';
      this.isAlipayQrCode = false;
      
      try {
        // 首先检测客户端类型
        const clientDetectResponse = await api.get('/yisheng/detect-client');
        const clientType = clientDetectResponse.data.clientType;
        
        console.log('客户端类型:', clientType);
        
        if (clientType === 'wechat') {
          // 微信客户端，检查是否已经有openId
          const openId = this.getOpenIdFromStorage();
          if (openId) {
            // 如果已经有openId，直接发起微信支付
            await this.processWechatPaymentWithOpenId(openId, amountInCents);
          } else {
            // 如果没有openId，使用微信网页授权
            await this.wechatOAuth();
          }
        } else if (clientType === 'alipay') {
          // 支付宝客户端，调用支付宝支付并直接跳转
          await this.processAlipayPayment(amountInCents);
        } else {
          // 其他浏览器，直接跳转到支付宝支付页面（而不是显示二维码）
          await this.processBrowserPayment(amountInCents);
        }
      } catch (error) {
        console.error('支付处理失败:', error);
        this.result = {
          type: 'error',
          message: '支付处理失败: ' + (error.response?.data?.message || error.message)
        };
      } finally {
        this.loading = false;
      }
    },
    
    // 微信网页授权
    async wechatOAuth() {
      try {
        // 直接跳转到后端提供的微信授权接口
        window.location.href = '/api/wechat/authorize';
      } catch (error) {
        console.error('微信授权失败:', error);
        this.result = {
          type: 'error',
          message: '微信授权失败: ' + error.message
        };
      }
    },
    
    async processWechatPaymentWithOpenId(openId, amountInCents) {
      try {
        // 保存openId到本地存储
        this.saveOpenIdToStorage(openId);
        
        // 使用获取到的openId调用拉卡拉微信支付
        const response = await api.post('/lakala/wechat/preorder', {
          userId: openId,
          outTradeNo: 'ORDER_' + Date.now() + '_' + Math.floor(Math.random() * 1000),
          totalAmount: amountInCents.toString()
        });
        
        if (response.data.success) {
          // 检查是否返回了微信支付参数
          if (response.data.wxRespParamInfo && response.data.wxRespParamInfo.wcPayData) {
            // 解析微信支付参数
            const wxPayData = JSON.parse(response.data.wxRespParamInfo.wcPayData);
            
            // 调用微信支付
            this.invokeWechatPay(wxPayData);
          } else {
            this.result = {
              type: 'success',
              message: '支付请求成功，正在调起微信支付...'
            };
          }
        } else {
          throw new Error(response.data.message);
        }
      } catch (error) {
        // 如果拉卡拉支付失败，回退到易生支付
        console.warn('拉卡拉微信支付失败，回退到易生支付:', error);
        try {
          // 使用获取到的openId调用易生微信JSAPI支付
          const fallbackResponse = await api.post('/yisheng/jsapi-pay', {
            subOpenId: openId,
            transAmount: amountInCents,
            orderSub: '支付给VNOcan'
          });
          
          if (fallbackResponse.data.success) {
            // 检查是否返回了微信支付参数
            if (fallbackResponse.data.wxRespParamInfo && fallbackResponse.data.wxRespParamInfo.wcPayData) {
              // 解析微信支付参数
              const wxPayData = JSON.parse(fallbackResponse.data.wxRespParamInfo.wcPayData);
              
              // 调用微信支付
              this.invokeWechatPay(wxPayData);
            } else {
              this.result = {
                type: 'success',
                message: '支付请求成功，正在调起微信支付...'
              };
            }
          } else {
            throw new Error(fallbackResponse.data.message);
          }
        } catch (fallbackError) {
          this.result = {
            type: 'error',
            message: '微信支付失败: ' + (fallbackError.response?.data?.message || fallbackError.message)
          };
        }
      }
    },
    
    // 调用微信支付的方法
    invokeWechatPay(wxPayData) {
      console.log('调用微信支付:', wxPayData);
      
      // 检查微信JSAPI是否可用
      if (typeof WeixinJSBridge === 'undefined') {
        this.result = {
          type: 'error',
          message: '请在微信客户端中打开'
        };
        return;
      }
      
      // 使用微信JSAPI调起支付
      WeixinJSBridge.invoke(
        'getBrandWCPayRequest',
        {
          appId: wxPayData.appId,
          timeStamp: wxPayData.timeStamp,
          nonceStr: wxPayData.nonceStr,
          package: wxPayData.package,
          signType: wxPayData.signType,
          paySign: wxPayData.paySign
        },
        (res) => {
          console.log('微信支付结果:', res);
          if (res.err_msg === "get_brand_wcpay_request:ok") {
            // 支付成功
            this.result = {
              type: 'success',
              message: '支付成功'
            };
          } else if (res.err_msg === "get_brand_wcpay_request:cancel") {
            // 用户取消支付
            this.result = {
              type: 'error',
              message: '用户取消支付'
            };
          } else {
            // 支付失败
            this.result = {
              type: 'error',
              message: '支付失败: ' + res.err_msg
            };
          }
        }
      );
    },
    
    async processAlipayPayment(amountInCents) {
      try {
        // 调用易生支付的扫码支付接口
        const response = await api.post('/yisheng/native-pay', {
          payType: 'AliPayNative',
          transAmount: amountInCents,
          orderSub: '支付给VNOcan'
        });
        
        if (response.data.success) {
          // 直接跳转到支付宝支付页面
          if (response.data.qrCodeUrl) {
            window.location.href = response.data.qrCodeUrl;
          } else {
            throw new Error('未返回有效的支付链接');
          }
        } else {
          throw new Error(response.data.message);
        }
      } catch (error) {
        throw new Error('支付宝支付初始化失败: ' + error.message);
      }
    },
    
    // 在浏览器中直接跳转到支付宝支付页面
    async processBrowserPayment(amountInCents) {
      try {
        // 调用易生支付的扫码支付接口
        const response = await api.post('/yisheng/native-pay', {
          payType: 'AliPayNative',
          transAmount: amountInCents,
          orderSub: '支付给VNOcan'
        });
        
        if (response.data.success) {
          // 直接跳转到支付宝支付页面（与支付宝客户端处理方式一致）
          if (response.data.qrCodeUrl) {
            window.location.href = response.data.qrCodeUrl;
          } else {
            throw new Error('未返回有效的支付链接');
          }
        } else {
          throw new Error(response.data.message);
        }
      } catch (error) {
        this.result = {
          type: 'error',
          message: '支付宝支付初始化失败: ' + error.message
        };
      }
    },
    
    // 拉卡拉微信支付处理方法
    async processLakalaWechatPayment() {
      // 验证金额
      if (!this.amount || this.amount <= 0) {
        this.result = {
          type: 'error',
          message: '请输入有效的支付金额'
        };
        return;
      }
      
      // 将元转换为分
      const amountInCents = Math.round(this.amount * 100);
      
      this.loading = true;
      this.result = { type: '', message: '' };
      this.qrCodeUrl = '';
      this.isAlipayQrCode = false;
      
      try {
        // 检查是否在微信环境中
        const clientDetectResponse = await api.get('/yisheng/detect-client');
        const clientType = clientDetectResponse.data.clientType;
        
        if (clientType === 'wechat') {
          // 在微信环境中，检查是否有openId
          const openId = this.getOpenIdFromStorage();
          if (openId) {
            // 直接调用拉卡拉微信支付
            await this.invokeLakalaWechatPay(openId, amountInCents);
          } else {
            // 没有openId，先进行微信授权
            await this.wechatOAuth();
          }
        } else {
          // 非微信环境，提示需要在微信中打开
          this.result = {
            type: 'error',
            message: '拉卡拉微信支付需要在微信客户端中使用，请在微信中打开此页面'
          };
        }
      } catch (error) {
        console.error('拉卡拉微信支付处理失败:', error);
        this.result = {
          type: 'error',
          message: '拉卡拉微信支付处理失败: ' + (error.response?.data?.message || error.message)
        };
      } finally {
        this.loading = false;
      }
    },
    
    // 调用拉卡拉微信支付
    async invokeLakalaWechatPay(openId, amountInCents) {
      try {
        // 调用拉卡拉微信支付接口
        const response = await api.post('/lakala/wechat/preorder', {
          userId: openId,
          outTradeNo: 'ORDER_' + Date.now() + '_' + Math.floor(Math.random() * 1000),
          totalAmount: amountInCents.toString()
        });
        
        if (response.data.success) {
          // 检查是否返回了微信支付参数
          if (response.data.wxRespParamInfo && response.data.wxRespParamInfo.wcPayData) {
            // 解析微信支付参数
            const wxPayData = JSON.parse(response.data.wxRespParamInfo.wcPayData);
            
            // 调用微信支付
            this.invokeWechatPay(wxPayData);
          } else {
            this.result = {
              type: 'success',
              message: '支付请求成功，正在调起微信支付...'
            };
          }
        } else {
          throw new Error(response.data.message);
        }
      } catch (error) {
        this.result = {
          type: 'error',
          message: '拉卡拉微信支付失败: ' + (error.response?.data?.message || error.message)
        };
      }
    }
  }
};
</script>

<style scoped>
.container {
  max-width: 500px;
  margin: 0 auto;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  padding: 30px;
}
h1 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
}
.payment-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.payment-info {
  text-align: center;
  margin-bottom: 20px;
}
.payee {
  font-size: 18px;
  color: #333;
  font-weight: bold;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
label {
  font-weight: bold;
  color: #555;
}
input {
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
  box-sizing: border-box;
}
.pay-button {
  background-color: #07c160;
  color: white;
  border: none;
  padding: 15px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 18px;
  transition: background-color 0.3s;
  width: 100%;
}
.pay-button:hover:not(:disabled) {
  background-color: #06ad56;
}
.pay-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}
.result {
  margin-top: 20px;
  padding: 15px;
  border-radius: 5px;
  text-align: center;
}
.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}
.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}
.info {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}
.qr-code-container {
  margin-top: 20px;
}
.qr-code {
  max-width: 200px;
  height: auto;
  margin: 0 auto;
  display: block;
  border: 1px solid #ddd;
  padding: 10px;
  background: white;
}
.alipay-qr-container {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
}
.qr-iframe {
  width: 220px;
  height: 220px;
  border: 1px solid #ddd;
  background: white;
}
.qr-link-text {
  font-size: 12px;
  color: #666;
  word-break: break-all;
  margin-top: 10px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.lakala-wechat-pay-button {
  background-color: #1aad19; /* 微信绿色 */
  color: white;
  padding: 15px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 18px;
  transition: background-color 0.3s;
  width: 100%;
  margin-top: 10px;
}

.lakala-wechat-pay-button:hover:not(:disabled) {
  background-color: #179b16;
}

.lakala-wechat-pay-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}
</style>